<script setup lang="ts">
import type { AlbumList, PageResult } from '~/types/blog'
import BlogAlbumLayoutSwitcher from '~/components/blog/AlbumLayoutSwitcher.vue'
import { normalizeFieldText } from '~/composables/useCustomFields'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { shouldShowAlbumSkeleton } from '~/utils/albumLoading.mjs'
import { albumListPlaceholderHeights } from '~/utils/masonry.mjs'

const blogApi = useBlogApi()
const route = useRoute()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const { layout: albumLayout, columns: albumLayoutColumns, batchSize } = useAlbumLayout('list')

const albums = shallowRef<AlbumList[]>([])
const batchAlbums = shallowRef<AlbumList[]>([])
const batchStart = ref(0)
const total = ref(0)
const nextPage = ref(2)
const loadingMore = ref(false)
const loadMoreFailed = ref(false)
const hasInitialBatch = ref(false)
const hydrated = ref(false)
const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null
let requestGeneration = 0

// 切换布局清空列表，按新页大小从第一批重新加载
watch(batchSize, () => {
  requestGeneration += 1
  albums.value = []
  batchAlbums.value = []
  batchStart.value = 0
  total.value = 0
  nextPage.value = 2
  loadingMore.value = false
  loadMoreFailed.value = false
  hasInitialBatch.value = false
}, { flush: 'sync' })

const { data: firstPage, pending, error: firstPageError, refresh } = await useAsyncData<PageResult<AlbumList>>(
  () => `albums:first-page:${batchSize.value}`,
  () => blogApi.getAlbumList({ page: 1, pageSize: batchSize.value }),
  { lazy: true }
)

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const pageDesc = normalizeFieldText(siteData?.setting?.customFields, 'albumsPageDescription', '浏览公开相册内容。')
const albumLocation = (album: { customFields?: Record<string, unknown> }) =>
  normalizeFieldText(album.customFields, 'location', '')

const {
  imagesSettled,
  markImageLoaded: markCoverLoaded,
  markImageFailed: markCoverFailed,
  isImageLoaded: isCoverLoaded,
  isImageFailed: isCoverFailed
} = useAlbumImageLoading<AlbumList>(computed(() => batchAlbums.value), album => Boolean(album.cover))

const batchSet = computed(() => new Set(batchAlbums.value))
const hasMore = computed(() => hasInitialBatch.value && albums.value.length < total.value)
const showAlbumSkeleton = computed(() => pending.value || (!firstPageError.value && (!hasInitialBatch.value || shouldShowAlbumSkeleton(false, hydrated.value, batchStart.value > 0 || imagesSettled.value))))

// isBatchLoading 当前批次全部加载后再统一显示卡片
const isBatchLoading = (album: AlbumList) => !imagesSettled.value && batchSet.value.has(album)

watch(firstPage, payload => {
  if (!payload || payload.pageSize !== batchSize.value) return
  requestGeneration += 1
  batchStart.value = 0
  batchAlbums.value = payload.list || []
  albums.value = batchAlbums.value
  total.value = payload.total || 0
  nextPage.value = 2
  hasInitialBatch.value = true
  loadMoreFailed.value = false
}, { immediate: true, flush: 'sync' })

// loadMore 按当前布局追加下一页，忽略切换布局前的旧请求
const loadMore = async () => {
  if (pending.value || loadingMore.value || !imagesSettled.value || !hasMore.value) return
  const generation = requestGeneration
  loadingMore.value = true
  loadMoreFailed.value = false
  try {
    const payload = await blogApi.getAlbumList({ page: nextPage.value, pageSize: batchSize.value })
    if (generation !== requestGeneration) return
    batchStart.value = albums.value.length
    batchAlbums.value = payload.list || []
    albums.value = [...albums.value, ...batchAlbums.value]
    total.value = payload.total || 0
    nextPage.value += 1
  } catch {
    // 请求失败后暂停自动加载，等待用户重试
    if (generation === requestGeneration) loadMoreFailed.value = true
  } finally {
    if (generation === requestGeneration) loadingMore.value = false
  }
}

// bindObserver 首批及当前图片加载完成后观察下拉位置
const bindObserver = () => {
  if (!import.meta.client) return
  observer?.disconnect()
  observer = null
  if (!sentinel.value || !hasMore.value || showAlbumSkeleton.value || loadingMore.value || loadMoreFailed.value || !imagesSettled.value) return
  observer = new IntersectionObserver(entries => {
    if (entries[0]?.isIntersecting) void loadMore()
  }, { rootMargin: '260px 0px' })
  observer.observe(sentinel.value)
}

watch([sentinel, hasMore, imagesSettled, loadingMore, loadMoreFailed, pending, firstPageError], bindObserver, { flush: 'post' })
onMounted(() => {
  hydrated.value = true
  bindObserver()
})
onBeforeUnmount(() => {
  requestGeneration += 1
  observer?.disconnect()
})

useSeoMeta({
  title: `相册 - ${siteName}`,
  description: pageDesc,
  ogTitle: `相册 - ${siteName}`,
  ogDescription: pageDesc
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <section class="albums-page" :class="{ 'albums-dense': albumLayout === 'dense' }">
    <div class="card header-card">
      <h1>相册</h1>
      <p>{{ pageDesc }}</p>
      <div class="header-actions">
        <BlogAlbumLayoutSwitcher
          v-model="albumLayout"
          :disabled="showAlbumSkeleton || loadingMore || !imagesSettled"
        />
      </div>
    </div>

    <BlogAlbumSkeleton
      v-if="showAlbumSkeleton"
      variant="list"
      :columns="albumLayoutColumns"
      :count="pending ? batchSize : batchAlbums.length || batchSize"
    />

    <!-- @vue-generic {AlbumList} -->
    <BlogMasonry
      v-if="albums.length"
      v-show="!showAlbumSkeleton"
      :key="batchSize"
      :items="albums"
      :columns="albumLayoutColumns"
      :tablet-columns="albumLayoutColumns"
      :gap="14"
      :estimated-heights="albumListPlaceholderHeights"
      class="album-grid"
    >
      <template #default="{ item: album, index }">
        <BlogAlbumSkeleton
          v-if="isBatchLoading(album)"
          variant="list"
          :columns="albumLayoutColumns"
          :count="1"
          :start-index="index"
          single
        />
        <NuxtLink v-show="!isBatchLoading(album)" :to="`/albums/${album.slug}`" class="card album-card">
          <div class="album-cover" :class="{ 'is-loaded': !album.cover || isCoverLoaded(album), 'is-error': isCoverFailed(album) }">
            <ClientOnly>
              <BlogImage
                v-if="album.cover"
                :src="resolveImageAssetUrl(album.cover)"
                :alt="album.title"
                loading="eager"
                :progressive="false"
                class="cover album-image"
                @load="markCoverLoaded(album)"
                @error="markCoverFailed(album)"
              />
              <div v-else class="cover-placeholder" aria-hidden="true">
                <i class="fa-regular fa-image" />
              </div>
              <BlogAlbumImageWarning v-if="isCoverFailed(album)" />
            </ClientOnly>
          </div>
          <div class="album-overlay">
            <h2>{{ album.title }}</h2>
            <p v-if="albumLayout !== 'dense'">{{ album.description || '暂无描述' }}</p>
            <div class="album-stats">
              <span><i class="fa-regular fa-image" aria-hidden="true" />{{ album.itemCount }} 张图片</span>
              <span><i class="fa-regular fa-heart" aria-hidden="true" />{{ album.likeCount || 0 }} 点赞</span>
              <span v-if="albumLayout !== 'dense' && albumLocation(album)">
                <i class="fa-solid fa-location-dot" aria-hidden="true" />
                {{ albumLocation(album) }}
              </span>
            </div>
          </div>
        </NuxtLink>
      </template>
    </BlogMasonry>

    <div v-else-if="firstPageError && !showAlbumSkeleton" class="empty-card load-error" role="alert">
      <span>相册加载失败</span>
      <button type="button" class="load-retry" @click="refresh()">重试</button>
    </div>
    <div v-else-if="!showAlbumSkeleton" class="empty-card">暂无相册</div>

    <BlogAlbumSkeleton
      v-if="loadingMore"
      variant="list"
      :columns="albumLayoutColumns"
      :count="batchSize"
    />
    <div v-if="loadMoreFailed" class="empty-card load-error" role="alert">
      <span>更多相册加载失败</span>
      <button type="button" class="load-retry" @click="loadMore">重试</button>
    </div>
    <div v-if="hasMore && !showAlbumSkeleton" ref="sentinel" class="scroll-sentinel" />
  </section>
</template>

<style scoped>
.albums-page {
  display: grid;
  gap: 14px;
}

.header-card {
  padding: 20px;
}

.header-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

h1 {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--heading-title-size);
}

.header-card p {
  margin-top: 8px;
  color: var(--text-soft);
}

.album-grid {
  width: 100%;
}

.scroll-sentinel {
  width: 100%;
  height: 1px;
}

.load-error {
  display: grid;
  justify-items: center;
  gap: 12px;
}

.load-retry {
  padding: 6px 16px;
  border: 0;
  border-radius: var(--radius);
  color: var(--brand);
  background: var(--brand-soft);
  cursor: pointer;
}

.album-card {
  position: relative;
  display: block;
  margin-bottom: 0;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  text-decoration: none;
  break-inside: avoid;
  page-break-inside: avoid;
}

.album-cover {
  position: relative;
  min-height: var(--album-placeholder-height);
  overflow: hidden;
  background: var(--empty-bg);
}

.album-cover.is-loaded {
  min-height: 0;
}

.album-card .album-image:not(.is-loaded),
.album-card .album-image.is-error {
  height: var(--album-placeholder-height);
}

.cover-placeholder {
  display: grid;
  min-height: var(--album-placeholder-height);
  place-items: center;
  color: color-mix(in srgb, var(--text-soft) 58%, transparent);
  font-size: 42px;
  line-height: 1;
}

.cover {
  display: block;
  width: 100%;
  height: auto;
}

.album-overlay {
  position: absolute;
  inset: auto 0 0;
  padding: 16px;
  background: linear-gradient(
    180deg,
    rgba(10, 16, 24, 0) 0%,
    rgba(10, 16, 24, 0.24) 38%,
    rgba(10, 16, 24, 0.58) 72%,
    rgba(10, 16, 24, 0.78) 100%
  );
  backdrop-filter: blur(1px);
}

.album-overlay h2 {
  font-size: 24px;
}

.album-overlay p {
  margin-top: 6px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.86);
}

.album-overlay span {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.album-stats {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.albums-dense .album-overlay {
  padding: 12px;
}

.albums-dense .album-overlay h2 {
  overflow: hidden;
  font-size: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.albums-dense .album-stats {
  margin-top: 6px;
  gap: 6px;
}

.albums-dense .album-stats span {
  margin-top: 0;
  font-size: 11px;
}

</style>
